import { KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCamera, FiClock, FiMinus, FiPause, FiPlus, FiPrinter, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import CameraScanner from '../components/CameraScanner';
import { Badge, ErrorBox, Field, Modal, useToast } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../i18n';
import api, { errorDetail, errorMessage } from '../services/api';
import type { Bill, Product, ReceiptData } from '../types';
import { useLiveEvents } from '../utils/live';
import { formatINR, formatQty, includedTax, rupeesToPaise } from '../utils/money';
import { printHtml } from '../utils/print';
import { invoiceA4Html, receiptHtml } from '../utils/receipt';
import { useBarcodeScanner } from '../utils/scanner';
import { findByBarcode, searchProducts } from '../utils/search';

interface Line { key: string; product: Product; qty: number; discount_paise: number; price_paise?: number; barcode?: string }
interface Quote { total_paise: number; gross_paise: number; line_discount_paise: number; bill_discount_paise: number;
  tax_paise: number; cgst_paise: number; sgst_paise: number }
interface PayRow { mode: 'cash' | 'card'; amount: string; reference: string }

const newRef = () => (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/-/g, '');

async function printBill(billId: number, widthMm: number, a4: boolean) {
  const { data } = await api.get<ReceiptData>(`/bills/${billId}/receipt`);
  await printHtml(a4 && data.gst_invoice ? invoiceA4Html(data) : receiptHtml(data, widthMm));
}

export default function Billing() {
  const { session, has, isRole, setting } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const [billDiscount, setBillDiscount] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteError, setQuoteError] = useState('');
  const [payOpen, setPayOpen] = useState<null | 'cash' | 'card' | 'split'>(null);
  const [heldOpen, setHeldOpen] = useState(false);
  const [held, setHeld] = useState<{ id: number; label: string | null; cart: { items: Line[] }; user_name: string; created_at: string }[]>([]);
  const [camera, setCamera] = useState(false);
  const [unknown, setUnknown] = useState<string | null>(null);
  const [short, setShort] = useState<{ name: string; size: string | null; needed: number; available: number }[] | null>(null);
  const [lastBill, setLastBill] = useState<Bill | null>(null);
  const clientRef = useRef(newRef());
  const scanRef = useRef<HTMLInputElement>(null);

  const weighed = has('weighed_items');
  const priceEdit = has('price_edit');
  const maxDisc = setting<number>('max_discount_percent', 0);
  const canDiscount = isRole('owner') || maxDisc > 0;
  const width = Number(session?.counter?.printer?.width_mm || 80);
  const readOnly = !!session?.read_only;

  const loadProducts = useCallback(async () => {
    const { data } = await api.get('/products', { params: { active: true, limit: 5000 } });
    setProducts(data.items);
  }, []);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Live stock from every counter.
  useLiveEvents((e) => {
    if (e.kind === 'stock') {
      const m = new Map<number, number>(e.payload.products.map((p: { id: number; stock_milli: number }) => [p.id, p.stock_milli]));
      const upd = (p: Product) => (m.has(p.id) ? { ...p, stock_milli: m.get(p.id)!, stock: m.get(p.id)! / 1000 } : p);
      setProducts((ps) => ps.map(upd));
      setLines((ls) => ls.map((l) => ({ ...l, product: upd(l.product) })));
    } else if (e.kind === 'catalog') {
      loadProducts();
    }
  });

  const focusScan = () => setTimeout(() => scanRef.current?.focus(), 0);

  const addProduct = useCallback((p: Product, qty = 1, barcode?: string) => {
    setLines((ls) => {
      const i = ls.findIndex((l) => l.product.id === p.id && !l.price_paise);
      if (i >= 0) {
        const copy = [...ls];
        copy[i] = { ...copy[i], qty: +(copy[i].qty + qty).toFixed(3) };
        return [...copy.slice(0, i), ...copy.slice(i + 1), copy[i]]; // scanned again: move to bottom
      }
      return [...ls, { key: newRef(), product: p, qty, discount_paise: 0, barcode }];
    });
    setQuery('');
    focusScan();
  }, []);

  const addByCode = useCallback(async (code: string) => {
    const c = code.trim();
    if (!c) return;
    const local = findByBarcode(products, c);
    if (local) { addProduct(local, 1, c); return; }
    try {
      const { data } = await api.get('/products/lookup', { params: { code: c } });
      let qty = 1;
      if (data.embedded) {
        qty = data.embedded.kind === 'weight' ? data.embedded.value / 1000
          : +(data.embedded.value / data.product.price_paise).toFixed(3);
      }
      addProduct(data.product, qty, c);
    } catch (err) {
      const d = errorDetail<{ can_link?: boolean }>(err);
      if (d?.can_link) setUnknown(c);
      else toast(isRole('cashier') ? t('bill.notFoundCashier') : errorMessage(err), 'error');
    }
  }, [products, addProduct, toast, isRole]);

  useBarcodeScanner(addByCode, !payOpen && !readOnly);

  const results = useMemo(() => (query && !/^\d{8,}$/.test(query) ? searchProducts(products, query) : []), [products, query]);
  useEffect(() => setHi(0), [query]);

  // Server-side pricing for the cart (debounced): the same maths that saves the bill.
  const billDiscountPaise = rupeesToPaise(billDiscount || '0') || 0;
  useEffect(() => {
    if (!lines.length) { setQuote(null); setQuoteError(''); return; }
    const id = setTimeout(() => {
      api.post('/bills/quote', {
        items: lines.map((l) => ({ product_id: l.product.id, qty: l.qty, discount_paise: l.discount_paise, unit_price_paise: l.price_paise })),
        bill_discount_paise: billDiscountPaise,
      }).then(({ data }) => { setQuote(data); setQuoteError(''); }).catch((e) => setQuoteError(errorMessage(e)));
    }, 200);
    return () => clearTimeout(id);
  }, [lines, billDiscountPaise]);

  const localTotal = lines.reduce((a, l) => a + Math.round((l.price_paise ?? l.product.price_paise) * l.qty) - l.discount_paise, 0) - billDiscountPaise;
  const total = quote?.total_paise ?? localTotal;
  const localTax = lines.reduce((a, l) => a + includedTax(Math.round((l.price_paise ?? l.product.price_paise) * l.qty) - l.discount_paise, l.product.gst_percent), 0);

  const setQty = (key: string, qty: number) => setLines((ls) => ls.flatMap((l) => (l.key !== key ? [l] : qty <= 0 ? [] : [{ ...l, qty }])));
  const clear = () => { setLines([]); setBillDiscount(''); clientRef.current = newRef(); focusScan(); };

  const hold = async () => {
    if (!lines.length) return;
    try {
      await api.post('/held-bills', { label: lines[0].product.name, cart: { items: lines } });
      toast('Bill held');
      clear();
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const openHeld = async () => {
    try { const { data } = await api.get('/held-bills'); setHeld(data); setHeldOpen(true); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const resume = async (id: number) => {
    if (lines.length) await hold();
    const { data } = await api.delete(`/held-bills/${id}`);
    const map = new Map(products.map((p) => [p.id, p]));
    setLines((data.cart.items as Line[]).map((l) => ({ ...l, product: map.get(l.product.id) || l.product })));
    setHeldOpen(false);
    focusScan();
  };

  const reprint = async () => {
    if (!lastBill) return;
    printBill(lastBill.id, width, false).catch((e) => toast(errorMessage(e), 'error'));
  };

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (payOpen || heldOpen || unknown || short) return;
      const map: Record<string, () => void> = {
        F2: () => lines.length && setPayOpen('cash'), F3: () => lines.length && setPayOpen('card'),
        F4: () => lines.length && setPayOpen('split'), F6: () => has('hold_resume') && hold(),
        F7: () => has('hold_resume') && openHeld(), F9: reprint,
      };
      if (map[e.key]) { e.preventDefault(); map[e.key](); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const onScanKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    const last = lines[lines.length - 1];
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[hi]) addProduct(results[hi]);
      else if (query) { addByCode(query); setQuery(''); }
      else if (lines.length) setPayOpen('cash');
    } else if (e.key === 'Escape') setQuery('');
    else if (!query && last && (e.key === '+' || e.key === '-')) { e.preventDefault(); setQty(last.key, last.qty + (e.key === '+' ? 1 : -1)); }
    else if (!query && last && e.key === 'Delete') setLines((ls) => ls.slice(0, -1));
  };

  const complete = async (payments: { mode: string; amount_paise: number; tendered_paise?: number; reference?: string }[],
    customer?: { name: string; phone: string; gstin: string }) => {
    const body = {
      client_ref: clientRef.current,
      items: lines.map((l) => ({ product_id: l.product.id, qty: l.qty, discount_paise: l.discount_paise,
        unit_price_paise: l.price_paise, barcode: l.barcode })),
      bill_discount_paise: billDiscountPaise, payments, customer,
    };
    try {
      const { data } = await api.post<Bill>('/bills', body);
      setLastBill(data);
      setPayOpen(null);
      toast(`${t('bill.done')}: ${data.bill_number}${data.change_paise ? ` · Change ${formatINR(data.change_paise)}` : ''}`);
      clear();
      if (setting('print_receipt_auto', true)) printBill(data.id, width, false).catch(() => toast('Receipt did not print – use Reprint', 'error'));
      return true;
    } catch (err) {
      const d = errorDetail<{ short_items?: typeof short }>(err);
      if (d?.short_items) { setPayOpen(null); setShort(d.short_items); }
      else toast(errorMessage(err), 'error');
      return false;
    }
  };

  if (!session) return null;
  return (
    <div className="flex h-full flex-col gap-3 p-3 lg:flex-row">
      <section className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="card relative p-3">
          <div className="flex gap-2">
            <input ref={scanRef} data-scan-target autoFocus className="input py-3 text-lg" placeholder={t('bill.scan')}
              value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onScanKey} disabled={readOnly}
              aria-label="Scan or search" />
            <button className="btn-secondary" onClick={() => setCamera(true)} title="Scan with camera" disabled={readOnly}><FiCamera size={20} /></button>
          </div>
          {results.length > 0 && (
            <ul className="absolute left-3 right-3 top-full z-20 mt-1 max-h-96 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
              {results.map((p, i) => (
                <li key={p.id}>
                  <button className={`flex w-full items-center justify-between px-4 py-2 text-left ${i === hi ? 'bg-brand-50' : 'hover:bg-slate-50'}`}
                    onMouseEnter={() => setHi(i)} onClick={() => addProduct(p)}>
                    <span><b>{p.name}</b> <span className="text-slate-500">{p.size} {p.brand && p.brand !== p.name ? `· ${p.brand}` : ''}</span></span>
                    <span className="flex items-center gap-3"><span className={p.stock <= 0 ? 'text-red-600' : 'text-slate-500'}>Stock {formatQty(p.stock)}</span>
                      <b>{formatINR(p.price_paise)}</b></span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card min-h-0 flex-1 overflow-y-auto">
          {!lines.length ? (
            <div className="flex h-full flex-col items-center justify-center p-10 text-center text-slate-400">
              <div className="text-xl">{t('bill.empty')}</div>
              <div className="mt-3 text-sm">
                <span className="kbd">F2</span> Cash · <span className="kbd">F3</span> Card · <span className="kbd">F4</span> Split
                {has('hold_resume') && <> · <span className="kbd">F6</span> Hold · <span className="kbd">F7</span> Held</>} · <span className="kbd">F9</span> Reprint
                · <span className="kbd">+</span>/<span className="kbd">-</span> last item qty · <span className="kbd">Del</span> remove last
              </div>
            </div>
          ) : (
            <table className="table">
              <thead><tr><th>#</th><th>Item</th><th className="text-center">Qty</th><th className="text-right">Price</th>
                {canDiscount && <th className="text-right">Disc ₹</th>}<th className="text-right">Amount</th><th /></tr></thead>
              <tbody>
                {lines.map((l, n) => {
                  const price = l.price_paise ?? l.product.price_paise;
                  const over = l.qty > l.product.stock && !has('negative_stock');
                  return (
                    <tr key={l.key} className={n === lines.length - 1 ? 'bg-brand-50/50' : ''}>
                      <td className="text-slate-400">{n + 1}</td>
                      <td><div className="font-semibold">{l.product.name} <span className="font-normal text-slate-500">{l.product.size}</span></div>
                        <div className={`text-xs ${over ? 'font-semibold text-red-600' : 'text-slate-400'}`}>Stock {formatQty(l.product.stock)}{over ? ' – not enough' : ''}</div></td>
                      <td>
                        <div className="flex items-center justify-center gap-1">
                          <button className="btn-secondary h-9 w-9 p-0" onClick={() => setQty(l.key, +(l.qty - 1).toFixed(3))} aria-label="Less"><FiMinus /></button>
                          <input className="input w-20 text-center text-lg font-semibold" inputMode="decimal" value={l.qty}
                            onChange={(e) => { const v = Number(e.target.value); if (!Number.isNaN(v)) setQty(l.key, weighed ? v : Math.round(v)); }} aria-label="Quantity" />
                          <button className="btn-secondary h-9 w-9 p-0" onClick={() => setQty(l.key, +(l.qty + 1).toFixed(3))} aria-label="More"><FiPlus /></button>
                        </div>
                      </td>
                      <td className="text-right">{priceEdit ? (
                        <input className="input w-24 text-right" defaultValue={(price / 100).toFixed(2)} aria-label="Price"
                          onBlur={(e) => { const p = rupeesToPaise(e.target.value); setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, price_paise: p && p !== x.product.price_paise ? p : undefined } : x))); }} />
                      ) : <>{formatINR(price)}{l.product.mrp_paise > price && <div className="text-xs text-slate-400 line-through">{formatINR(l.product.mrp_paise)}</div>}</>}</td>
                      {canDiscount && <td className="text-right"><input className="input w-20 text-right" inputMode="decimal" aria-label="Line discount"
                        value={l.discount_paise ? l.discount_paise / 100 : ''} onChange={(e) => setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, discount_paise: rupeesToPaise(e.target.value) || 0 } : x)))} /></td>}
                      <td className="text-right text-lg font-semibold">{formatINR(Math.round(price * l.qty) - l.discount_paise)}</td>
                      <td><button className="btn-ghost p-2 text-red-600" onClick={() => setQty(l.key, 0)} aria-label="Remove"><FiTrash2 /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <aside className="flex w-full flex-col gap-3 lg:w-96">
        <div className="card p-4">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>{session.counter?.name || 'Back office'} · {session.user.full_name}</span>
            {!session.counter && <Badge color="amber">No counter</Badge>}
          </div>
          <div className="mt-3 space-y-1 text-[15px]">
            <div className="flex justify-between"><span>{t('bill.items')}</span><span>{lines.length} ({formatQty(lines.reduce((a, l) => a + l.qty, 0))} qty)</span></div>
            {quote && (quote.line_discount_paise + quote.bill_discount_paise) > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Discount</span><span>-{formatINR(quote.line_discount_paise + quote.bill_discount_paise)}</span></div>)}
            {(quote?.tax_paise ?? localTax) > 0 && (
              <div className="flex justify-between text-slate-500"><span>Incl. GST (CGST+SGST)</span><span>{formatINR(quote?.tax_paise ?? localTax)}</span></div>)}
          </div>
          <div className="mt-3 flex items-end justify-between border-t border-slate-200 pt-3">
            <span className="text-lg">{t('bill.total')}</span>
            <span className="text-4xl font-bold text-brand-800" data-testid="bill-total">{formatINR(total)}</span>
          </div>
          {canDiscount && lines.length > 0 && (
            <Field label={`${t('bill.discount')} (₹)${isRole('owner') ? '' : ` · max ${maxDisc}%`}`} className="mt-3">
              <input className="input" inputMode="decimal" value={billDiscount} onChange={(e) => setBillDiscount(e.target.value)} />
            </Field>
          )}
          {quoteError && <div className="mt-2 text-sm text-red-600">{quoteError}</div>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button className="btn-primary btn-big col-span-2 text-xl" disabled={!lines.length || readOnly || !!quoteError} onClick={() => setPayOpen('cash')}>
            {t('bill.cash')} <span className="kbd bg-brand-800 text-brand-100">F2</span></button>
          <button className="btn-secondary btn-big" disabled={!lines.length || readOnly || !!quoteError} onClick={() => setPayOpen('card')}>{t('bill.card')} <span className="kbd">F3</span></button>
          <button className="btn-secondary btn-big" disabled={!lines.length || readOnly || !!quoteError} onClick={() => setPayOpen('split')}>{t('bill.split')} <span className="kbd">F4</span></button>
          {has('hold_resume') && <>
            <button className="btn-secondary" disabled={!lines.length || readOnly} onClick={hold}><FiPause /> {t('bill.hold')}</button>
            <button className="btn-secondary" disabled={readOnly} onClick={openHeld}><FiClock /> {t('bill.held')}</button></>}
          <button className="btn-secondary" disabled={!lastBill} onClick={reprint}><FiPrinter /> {t('bill.reprint')}</button>
          <button className="btn-ghost text-red-600" disabled={!lines.length} onClick={clear}><FiTrash2 /> {t('bill.clear')}</button>
        </div>
        {lastBill && <div className="text-center text-sm text-slate-500">Last bill {lastBill.bill_number} · {formatINR(lastBill.total_paise)}</div>}
      </aside>

      <PaymentModal mode={payOpen} total={total} onClose={() => { setPayOpen(null); focusScan(); }} onComplete={complete}
        customerFields={has('customer_details')} />

      <Modal open={heldOpen} onClose={() => setHeldOpen(false)} title={t('bill.held')}>
        {!held.length ? <p className="text-slate-500">No held bills.</p> : (
          <table className="table"><tbody>{held.map((h) => (
            <tr key={h.id}><td><b>{h.label}</b> <span className="text-slate-500">+{h.cart.items.length - 1} more</span></td>
              <td>{h.user_name}</td><td>{new Date(h.created_at).toLocaleTimeString('en-IN')}</td>
              <td><button className="btn-primary px-3 py-1" onClick={() => resume(h.id)}>Resume</button></td></tr>))}</tbody></table>
        )}
      </Modal>

      <Modal open={!!short} onClose={() => { setShort(null); focusScan(); }} title={t('bill.short')} size="sm"
        footer={<button className="btn-primary" onClick={() => { setShort(null); focusScan(); }}>OK</button>}>
        <p className="mb-2">The bill was not saved. Reduce these items:</p>
        <ul className="list-disc pl-5">{short?.map((s) => <li key={s.name}><b>{s.name} {s.size}</b>: need {s.needed}, only {s.available} left</li>)}</ul>
      </Modal>

      <Modal open={!!unknown} onClose={() => { setUnknown(null); focusScan(); }} title={`Unknown barcode ${unknown}`} size="sm">
        <p className="mb-3 text-slate-600">This barcode is not linked to any product.</p>
        <div className="flex flex-col gap-2">
          <button className="btn-primary" onClick={() => navigate(`/products?new=1&barcode=${encodeURIComponent(unknown || '')}`)}>Create new product with this barcode</button>
          <LinkToExisting code={unknown} products={products} onDone={(p) => { setUnknown(null); loadProducts(); addProduct(p, 1, unknown || undefined); }} />
        </div>
      </Modal>

      <CameraScanner open={camera} onClose={() => { setCamera(false); focusScan(); }} onScan={addByCode} continuous />
    </div>
  );
}

function LinkToExisting({ code, products, onDone }: { code: string | null; products: Product[]; onDone: (p: Product) => void }) {
  const [q, setQ] = useState('');
  const toast = useToast();
  const res = searchProducts(products, q, 8);
  const link = async (p: Product) => {
    try { await api.post(`/products/${p.id}/barcodes`, { code }); toast(`Linked to ${p.name}`); onDone(p); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-2 font-medium">Or link it to an existing product</div>
      <input className="input" placeholder="Search product" value={q} onChange={(e) => setQ(e.target.value)} />
      <ul className="mt-2">{res.map((p) => (
        <li key={p.id}><button className="w-full rounded px-2 py-1 text-left hover:bg-slate-100" onClick={() => link(p)}>{p.name} {p.size}</button></li>))}</ul>
    </div>
  );
}

function PaymentModal({ mode, total, onClose, onComplete, customerFields }: {
  mode: null | 'cash' | 'card' | 'split'; total: number; onClose: () => void; customerFields: boolean;
  onComplete: (p: { mode: string; amount_paise: number; tendered_paise?: number; reference?: string }[],
    c?: { name: string; phone: string; gstin: string }) => Promise<boolean>;
}) {
  const [received, setReceived] = useState('');
  const [reference, setReference] = useState('');
  const [rows, setRows] = useState<PayRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [customer, setCustomer] = useState({ name: '', phone: '', gstin: '' });
  const [err, setErr] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!mode) return;
    setReceived(''); setReference(''); setErr(''); setBusy(false);
    setRows([{ mode: 'cash', amount: (total / 100).toFixed(2), reference: '' }, { mode: 'card', amount: '', reference: '' }]);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [mode, total]);

  const tendered = rupeesToPaise(received || '') ?? total;
  const change = tendered - total;
  const splitSum = rows.reduce((a, r) => a + (rupeesToPaise(r.amount || '0') || 0), 0);

  const submit = async () => {
    if (busy) return; // a bill is never submitted twice from a double click
    setErr('');
    let payments: { mode: string; amount_paise: number; tendered_paise?: number; reference?: string }[];
    if (mode === 'cash') {
      if (tendered < total) { setErr('Cash received is less than the total'); return; }
      payments = [{ mode: 'cash', amount_paise: total, tendered_paise: tendered }];
    } else if (mode === 'card') {
      payments = [{ mode: 'card', amount_paise: total, reference: reference || undefined }];
    } else {
      payments = rows.map((r) => ({ mode: r.mode, amount_paise: rupeesToPaise(r.amount || '0') || 0, reference: r.reference || undefined,
        ...(r.mode === 'cash' ? { tendered_paise: rupeesToPaise(r.amount || '0') || 0 } : {}) })).filter((p) => p.amount_paise > 0);
      if (splitSum !== total) { setErr(`Split adds up to ${formatINR(splitSum)}, bill is ${formatINR(total)}`); return; }
    }
    setBusy(true);
    const ok = await onComplete(payments, customerFields && (customer.name || customer.phone) ? customer : undefined);
    if (!ok) setBusy(false);
  };

  const notes = [100, 200, 500, 2000].filter((n) => n * 100 >= total).slice(0, 3);
  return (
    <Modal open={!!mode} onClose={onClose} title={`${mode === 'split' ? 'Split payment' : mode === 'card' ? 'Card payment' : 'Cash payment'} · ${formatINR(total)}`} size="sm"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel <span className="kbd">Esc</span></button>
        <button className="btn-primary btn-big" disabled={busy} onClick={submit} data-testid="complete-bill">{busy ? 'Saving…' : t('bill.complete')} <span className="kbd bg-brand-800 text-brand-100">Enter</span></button></>}>
      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <ErrorBox>{err}</ErrorBox>
        {mode === 'cash' && <>
          <Field label={t('bill.received')}><input ref={inputRef} className="input py-3 text-2xl" inputMode="decimal" value={received}
            placeholder={(total / 100).toFixed(2)} onChange={(e) => setReceived(e.target.value)} /></Field>
          <div className="mt-2 flex gap-2">{notes.map((n) => <button type="button" key={n} className="btn-secondary flex-1" onClick={() => setReceived(String(n))}>₹{n}</button>)}
            <button type="button" className="btn-secondary flex-1" onClick={() => setReceived((total / 100).toFixed(2))}>Exact</button></div>
          <div className={`mt-4 rounded-lg p-3 text-center ${change >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <div className="text-sm">{t('bill.change')}</div>
            <div className={`text-3xl font-bold ${change >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatINR(Math.max(change, 0))}</div>
          </div>
        </>}
        {mode === 'card' && <Field label="Card slip / last 4 digits (optional)"><input ref={inputRef} className="input" value={reference} onChange={(e) => setReference(e.target.value)} /></Field>}
        {mode === 'split' && <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2">
              <select className="input w-28" value={r.mode} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, mode: e.target.value as PayRow['mode'] } : x)))}>
                <option value="cash">Cash</option><option value="card">Card</option></select>
              <input ref={i === 0 ? inputRef : undefined} className="input" inputMode="decimal" placeholder="Amount ₹" value={r.amount}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
              <button type="button" className="btn-secondary whitespace-nowrap px-2 text-sm"
                onClick={() => setRows(rows.map((x, j) => (j === i ? { ...x, amount: ((total - splitSum + (rupeesToPaise(x.amount || '0') || 0)) / 100).toFixed(2) } : x)))}>Rest</button>
            </div>))}
          <div className={`text-sm ${splitSum === total ? 'text-emerald-700' : 'text-red-600'}`}>Entered {formatINR(splitSum)} of {formatINR(total)}</div>
        </div>}
        {customerFields && <div className="mt-4 grid grid-cols-2 gap-2">
          <input className="input" placeholder="Customer name (optional)" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
          <input className="input" placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
          <input className="input col-span-2" placeholder="Customer GSTIN (B2B)" value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value })} />
        </div>}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
